import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSSOTeamSyncConfig,
  updateSSOTeamSyncConfig,
  getSSOGroupMappings,
  createSSOGroupMapping,
  updateSSOGroupMapping,
  deleteSSOGroupMapping,
  previewSSOGroupMappings,
  getSSOAuditEvents,
  getSSOExternalGroups,
  getSSOTargets,
  uploadSSOExternalGroups,
  uploadSSOGroupMappings,
  resolveRoleConflict,
} from "@/helpers/apis";
import { GET_USER_LIST } from "@/services/constants";
import type { BaseUser } from "@/types";
import type {
  SSOTeamSyncConfigResponse,
  SSOTeamSyncConfigUpdatePayload,
  SSOGroupMappingUpdatePayload,
} from "./types";

// Query key constants
export const SSO_CONFIG_KEY = "SSO_TEAM_SYNC_CONFIG";
export const SSO_MAPPINGS_KEY = "SSO_GROUP_MAPPINGS";
export const SSO_TARGETS_KEY = "SSO_TARGETS";
export const SSO_AUDIT_EVENTS_KEY = "SSO_AUDIT_EVENTS";

// --- Config ---
export const useSSOTeamSyncConfig = () =>
  useQuery({
    queryKey: [SSO_CONFIG_KEY],
    queryFn: getSSOTeamSyncConfig,
  });

export const useUpdateSSOTeamSyncConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SSOTeamSyncConfigUpdatePayload) => updateSSOTeamSyncConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_CONFIG_KEY] });
    },
  });
};

// --- Mappings ---
export const useSSOGroupMappings = (mappingType?: string) =>
  useQuery({
    queryKey: [SSO_MAPPINGS_KEY, mappingType],
    queryFn: () => getSSOGroupMappings(mappingType ? { mapping_type: mappingType } : undefined),
  });

export const useCreateSSOGroupMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      external_group_identifier: string;
      match_type: string;
      mapping_type: string;
      target_id: number;
    }) => createSSOGroupMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_MAPPINGS_KEY] });
    },
  });
};

export const useUpdateSSOGroupMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mappingId, data }: { mappingId: number; data: SSOGroupMappingUpdatePayload }) =>
      updateSSOGroupMapping(mappingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_MAPPINGS_KEY] });
    },
  });
};

export const useDeleteSSOGroupMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: number) => deleteSSOGroupMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_MAPPINGS_KEY] });
    },
  });
};

// --- Targets (teams + roles for dropdowns) ---
export const useSSOTargets = () =>
  useQuery({
    queryKey: [SSO_TARGETS_KEY],
    queryFn: getSSOTargets,
  });

// --- Preview ---
export const usePreviewSSOGroupMappings = () =>
  useMutation({
    mutationFn: (data: { external_groups: string[] }) => previewSSOGroupMappings(data),
  });

// --- Config Form (local state + save) ---
export const useConfigForm = (config: SSOTeamSyncConfigResponse | undefined) => {
  const [form, setForm] = useState({
    provider: "other" as string,
    group_claim_name: "",
    group_prefix_filter: "",
    enable_graph_api_fallback: false,
    graph_api_tenant_id: "",
    enable_okta_api_fallback: false,
    okta_domain: "",
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        provider: config.provider ?? "other",
        group_claim_name: config.group_claim_name ?? "groups",
        group_prefix_filter: config.group_prefix_filter ?? "",
        enable_graph_api_fallback: config.enable_graph_api_fallback,
        graph_api_tenant_id: config.graph_api_tenant_id ?? "",
        enable_okta_api_fallback: config.enable_okta_api_fallback,
        okta_domain: config.okta_domain ?? "",
      });
      setDirty(false);
    }
  }, [config]);

  const update = (field: keyof typeof form, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const getPayload = () => ({
    provider: form.provider,
    group_claim_name: form.group_claim_name || null,
    group_prefix_filter: form.group_prefix_filter || null,
    enable_graph_api_fallback: form.enable_graph_api_fallback,
    graph_api_tenant_id: form.graph_api_tenant_id || null,
    enable_okta_api_fallback: form.enable_okta_api_fallback,
    okta_domain: form.okta_domain || null,
  });

  const markClean = () => setDirty(false);

  return { form, dirty, update, getPayload, markClean };
};

// --- External Groups ---
export const SSO_EXTERNAL_GROUPS_KEY = "SSO_EXTERNAL_GROUPS";

export const useSSOExternalGroups = (search?: string) =>
  useQuery({
    queryKey: [SSO_EXTERNAL_GROUPS_KEY, search],
    queryFn: () => getSSOExternalGroups(search ? { search } : undefined),
  });

export const useUploadSSOExternalGroups = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadSSOExternalGroups,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_EXTERNAL_GROUPS_KEY] });
    },
  });
};

export const useUploadSSOGroupMappings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadSSOGroupMappings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SSO_MAPPINGS_KEY] });
    },
  });
};

// --- Role Conflict Resolution ---
interface UserListData {
  users: BaseUser[];
  meta: { count: number; total_pages: number };
}

export const useResolveRoleConflict = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resolveRoleConflict,
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: [GET_USER_LIST] });

      const previousQueries = queryClient.getQueriesData<UserListData>({
        queryKey: [GET_USER_LIST],
      });

      queryClient.setQueriesData<UserListData>({ queryKey: [GET_USER_LIST] }, old => {
        if (!old) return old;
        return {
          ...old,
          users: old.users.map(u =>
            u.uuid === variables.user_uuid
              ? {
                  ...u,
                  has_role_conflict: false,
                  sso_resolved_role_id: null,
                  sso_resolved_role_title: null,
                  ...(variables.action === "accept_sso" && u.sso_resolved_role_title
                    ? { role_title: u.sso_resolved_role_title }
                    : {}),
                }
              : u
          ),
        };
      });

      return { previousQueries };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USER_LIST] });
    },
  });
};

// --- Audit Events ---
export const useSSOAuditEvents = (params: {
  user_id?: number;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}) =>
  useQuery({
    queryKey: [SSO_AUDIT_EVENTS_KEY, params],
    queryFn: () => getSSOAuditEvents(params),
  });
